"use client";

import { useEffect, useRef, type ReactNode } from "react";
import Image, { type ImageProps } from "next/image";

import { type Align, type RiveProps } from "@marketing-sites/ui/components";
import { useMediaQuery } from "@marketing-sites/hooks/use-media-query";
import { useScrollMask } from "@marketing-sites/hooks/use-scroll-mask";
import { Carousel } from "@marketing-sites/ui/primitives";
import { cn } from "@marketing-sites/utils/cn";

const alignToObjectPosition: Record<
  Align,
  React.CSSProperties["objectPosition"]
> = {
  Center: "center",
  TopLeft: "top left",
  TopCenter: "top center",
  TopRight: "top right",
  CenterLeft: "left",
  CenterRight: "right",
  BottomLeft: "bottom left",
  BottomCenter: "bottom",
  BottomRight: "bottom right",
};

type HeroImageMedia = {
  type: "image";
  width: number;
  height: number;
  align?: Align;
} & Pick<ImageProps, "src" | "alt" | "sizes">;

type HeroRiveMedia = {
  type: "rive";
  width: number;
  height: number;
} & Pick<
  RiveProps,
  "src" | "fit" | "align" | "stateMachineName" | "placeholder" | "staticImage"
>;

type HeroMedia = (HeroImageMedia | HeroRiveMedia) & {
  delay: number;
};

export interface HeroSlide {
  title: ReactNode;
  media: {
    small: HeroMedia;
    large: HeroMedia;
  };
  backdrop?: Pick<ImageProps, "src" | "alt" | "sizes">;
  /** Recolours this slide's thumbnail autoplay progress bar (default `bg-helpdesk`). */
  progressClassName?: string;
  analyticsId: string;
}

function SlideMedia({
  index,
  media,
  loading = "lazy",
}: {
  index: number;
  media: HeroMedia;
  loading?: "eager" | "lazy";
}) {
  return (
    <div
      className="relative size-full"
      style={{ aspectRatio: media.width / media.height }}
    >
      {media.type === "image" ? (
        <Image
          fill
          src={media.src}
          alt={media.alt}
          sizes={media.sizes}
          loading={loading}
          className="object-contain"
          style={{
            objectPosition: media.align
              ? alignToObjectPosition[media.align]
              : undefined,
          }}
        />
      ) : (
        <Carousel.RiveWebGL
          index={index}
          src={media.src}
          fit={media.fit}
          align={media.align}
          stateMachineName={media.stateMachineName}
          placeholder={media.placeholder}
          staticImage={media.staticImage}
        />
      )}
    </div>
  );
}

function HeroThumbnails({
  slides,
  scrollRef,
  maskImage,
}: {
  slides: HeroSlide[];
  scrollRef: (el: HTMLDivElement | null) => void;
  maskImage: string;
}) {
  const { emblaApi } = Carousel.useCarousel();
  const thumbRefs = useRef<(HTMLDivElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => {
      const index = emblaApi.selectedScrollSnap();
      const thumb = thumbRefs.current[index];
      const container = containerRef.current;
      if (!thumb || !container) return;

      const thumbLeft = thumb.offsetLeft;
      const thumbWidth = thumb.offsetWidth;
      const containerWidth = container.clientWidth;

      container.scrollTo({
        left: thumbLeft - containerWidth / 2 + thumbWidth / 2,
        behavior: "smooth",
      });
    };

    emblaApi.on("select", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi]);

  return (
    <div
      ref={(el) => {
        containerRef.current = el;
        scrollRef(el);
      }}
      className="overflow-x-auto min-[1370px]:overflow-visible"
      style={{
        maskImage,
        scrollbarWidth: "none",
      }}
    >
      <Carousel.Thumbnails className="flex gap-0">
        {slides.map((slide, index) => (
          <div
            key={index}
            ref={(el) => {
              thumbRefs.current[index] = el;
            }}
            className="flex-1 whitespace-nowrap first:*:ml-0"
          >
            <Carousel.Thumbnail
              index={index}
              // eslint-disable-next-line analytics/id-must-be-static -- each item in slides carries a hardcoded analyticsId
              analyticsId={slide.analyticsId}
              className="border-current/10 data-selected:z-1 data-selected:text-current data-selected:bg-off-white text-current/50 hover:text-current/75 hover:z-1 min-[1370px]:px-4! -ml-px w-full cursor-pointer border p-4 px-6 text-left first:ml-0 lg:px-10"
              progress={({ ref, isPlaying, isSelected }) => (
                <div
                  ref={ref}
                  className={cn(
                    "bg-helpdesk absolute left-0 top-0 h-0.5 w-full origin-left",
                    slide.progressClassName,
                  )}
                  style={{
                    animation: "carouselAutoplay linear",
                    opacity: isPlaying && isSelected ? 1 : 0,
                  }}
                />
              )}
            >
              <span className="text-body-lg-desktop xl:text-heading-6-desktop block">
                {slide.title}
              </span>
            </Carousel.Thumbnail>
          </div>
        ))}
      </Carousel.Thumbnails>
    </div>
  );
}

export default function HeroCarousel({
  slides,
  priority = false,
  className,
}: {
  slides: HeroSlide[];
  priority?: boolean;
  className?: string;
}) {
  const { ref: scrollRef, maskImage } = useScrollMask();
  const isLarge = useMediaQuery("(min-width: 768px)");

  return (
    <div className={cn("mt-15 grid grid-cols-12", className)}>
      <div className="col-span-full col-start-1 md:col-span-10 md:col-start-2">
        <Carousel.Root>
          <HeroThumbnails
            slides={slides}
            scrollRef={scrollRef}
            maskImage={maskImage}
          />
          <Carousel.Viewport>
            <Carousel.Container>
              {slides.map((slide, index) => {
                const isLcp = priority && index === 0;
                return (
                  <Carousel.Slide
                    key={index}
                    className="basis-full"
                    delay={
                      isLarge
                        ? slide.media.large.delay
                        : slide.media.small.delay
                    }
                  >
                    <div className="relative">
                      {slide.backdrop && (
                        <Image
                          src={slide.backdrop.src}
                          alt={slide.backdrop.alt ?? ""}
                          fill
                          sizes={slide.backdrop.sizes}
                          priority={isLcp}
                          loading={isLcp ? "eager" : "lazy"}
                          className="object-cover"
                        />
                      )}
                      <div className="relative md:hidden">
                        <SlideMedia
                          index={index}
                          loading={isLcp ? "eager" : "lazy"}
                          media={slide.media.small}
                        />
                      </div>
                      <div className="relative hidden md:block">
                        <SlideMedia
                          index={index}
                          loading={isLcp ? "eager" : "lazy"}
                          media={slide.media.large}
                        />
                      </div>
                    </div>
                  </Carousel.Slide>
                );
              })}
            </Carousel.Container>
          </Carousel.Viewport>
        </Carousel.Root>
      </div>
    </div>
  );
}
